import { useQueryClient } from "@tanstack/react-query";
import type { MessagesPage } from "@/lib/inbox";

export interface DialogPreview {
  text: string | null;
  hasMedia: boolean;
  media_kind: string | null;
  media_name: string | null;
}

/**
 * Best-effort last-message preview for the dialog list.
 *
 * The dialog summary carries no message text, and we must NOT add a write or
 * a backend round-trip just for previews (passive-read rule). So we reuse
 * whatever messages are already in the React Query cache (the selected
 * dialog, anything previously opened) and read the newest entry. Dialogs
 * with no cached messages return null → DialogListItem falls back to the
 * via-account meta line.
 *
 * This is purely a cache read — it triggers no fetch and bumps nothing.
 */
export function useDialogPreviews(): (dialogId: string) => DialogPreview | null {
  const qc = useQueryClient();

  return (dialogId: string) => {
    const entries = qc.getQueriesData<MessagesPage>({
      queryKey: ["inbox_messages", dialogId],
    });
    for (const [, data] of entries) {
      // API returns newest-first → index 0 is the latest message.
      const latest = data?.items?.[0];
      if (latest) {
        return {
          text: latest.text,
          hasMedia: latest.has_media,
          media_kind: latest.media_kind,
          media_name: latest.media_name,
        };
      }
    }
    return null;
  };
}
